/// JsonSchemaForm: Generic fallback form for user tasks with unknown formKeys.
/// Renders task variables as editable JSON text and provides Complete/Fail buttons.
/// Used when no specific form component is registered for a formKey.

import { useState } from "react";
import type { TaskFormProps } from "../TaskFormRouter";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function JsonSchemaForm({ task, onComplete, onFail }: TaskFormProps) {
  const [variables, setVariables] = useState(JSON.stringify(task.variables, null, 2));
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleComplete = async () => {
    try {
      const parsed = JSON.parse(variables);
      setError(null);
      setIsSubmitting(true);
      try {
        await onComplete(parsed);
      } finally {
        setIsSubmitting(false);
      }
    } catch {
      setError("Invalid JSON -- please fix the syntax");
    }
  };

  const handleFail = async () => {
    setIsSubmitting(true);
    try {
      await onFail("Failed by user");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Custom headers info */}
      {Object.keys(task.customHeaders).length > 0 && (
        <div>
          <label className="text-xs text-zinc-400 mb-1 block">Custom Headers</label>
          <pre className="bg-zinc-800 rounded p-2 text-[10px] text-zinc-400 max-h-16 overflow-y-auto font-mono">
            {JSON.stringify(task.customHeaders, null, 2)}
          </pre>
        </div>
      )}

      {/* Editable JSON variables */}
      <div>
        <label className="text-xs text-zinc-400 mb-1 block">Task Variables (JSON)</label>
        <textarea
          value={variables}
          onChange={(e) => {
            setVariables(e.target.value);
            setError(null);
          }}
          className="w-full h-40 rounded bg-zinc-800 p-3 font-mono text-xs text-zinc-200 focus:outline-none border border-zinc-600 resize-none"
        />
        {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
      </div>

      {/* Action buttons */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleComplete}
          disabled={isSubmitting}
          className="flex-1 bg-green-600 text-white py-2 rounded text-sm hover:bg-green-500 disabled:opacity-50 transition-colors"
        >
          Complete
        </button>
        <button
          type="button"
          onClick={handleFail}
          disabled={isSubmitting}
          className="bg-red-600 text-white px-4 py-2 rounded text-sm hover:bg-red-500 disabled:opacity-50 transition-colors"
        >
          Fail
        </button>
      </div>
    </div>
  );
}
